import React, { useEffect, useState } from 'react';
import { pick } from 'ramda';

import { Button } from '@interlayer/js/components/common/Button';

import { useTranslations } from '@core/js/hooks/i18n';
import { useUserSelector } from '@core/js/store/hooks';

import { useEnterDraw } from './hooks/useEnterDraw';
import { CTA_TYPES } from './constants';
import {
    TEnterDrawBareProps,
    TEnterDrawConfig,
    TEnterDrawParsedConfig,
    TEnterDrawProps,
    TEnterDrawSecondaryTag,
    TEnterDrawWebContentConfig,
    TEnterDrawWebContentTextMap
} from './types';
import { parseConfig } from './utils';

export const EnterDrawBare = <T extends boolean = false, >({
    isParseConfig,
    type,
    index,
    className,
    ...config
}: TEnterDrawBareProps<T>) => {
    const props: TEnterDrawParsedConfig = isParseConfig ? parseConfig(config as TEnterDrawConfig) : config;
    const t = useTranslations('playtech.mobile.system.enter-draw.');
    const {
        mainTag = '',
        secondaryTags = [],
        optedInCTAText = t('button.opted-in'),
        disabledCTAText = t('button.disabled'),
        optInCTAText = t('button.opt-in'),
        showLoginForGuest
    } = props;

    const { isLoggedIn, allPlayerTags: playerTags } = useUserSelector(pick(['isLoggedIn', 'allPlayerTags']));
    const [buttonText, setButtonText] = useState(optInCTAText);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    const handleButtonClick = useEnterDraw({ ...props, type, index });

    useEffect(() => {
        if (isLoggedIn && playerTags) {
            const isSet = playerTags[mainTag];
            const isDisabled = secondaryTags.find(({ secondaryTag }) => playerTags[secondaryTag]);

            if (isSet || isDisabled) {
                setIsButtonDisabled(true);
                setButtonText(isSet ? optedInCTAText : disabledCTAText);
            } else {
                setIsButtonDisabled(false);
                setButtonText(optInCTAText);
            }
        } else if (showLoginForGuest) {
            setIsButtonDisabled(false);
            setButtonText(optInCTAText);
        } else {
            setIsButtonDisabled(true);
            setButtonText(optInCTAText);
        }
    }, [
        mainTag,
        playerTags,
        isLoggedIn,
        optInCTAText,
        secondaryTags,
        optedInCTAText,
        disabledCTAText,
        showLoginForGuest
    ]);

    if (!secondaryTags.length && !mainTag) {
        return null;
    }

    return (
        <Button
            className={className}
            onClick={handleButtonClick}
            disabled={isButtonDisabled}
            data-testid="EnterDraw_button"
        >
            {buttonText}
        </Button>
    );
};

export const EnterDraw: React.FC<TEnterDrawProps> = ({ config }) => <EnterDrawBare isParseConfig {...config} />;

export const EnterDrawWebContent: React.FC<TEnterDrawWebContentConfig> = ({
    maintag,
    secondarytags = '',
    translationprefix,
    showloginforguest,
    showcontactpopup,
    url,
    gamecode,
    optinctatext = '',
    className
}) => {
    const t = useTranslations(`playtech.mobile.system.enter-draw.${translationprefix}.`);

    const secondaryTags: TEnterDrawSecondaryTag[] = secondarytags?.split(',').map(tag => ({ secondaryTag: tag.trim() }));
    const ctaConfiguration = url
        ? CTA_TYPES.navigate
        : gamecode ? CTA_TYPES.gameLaunch : undefined;

    let textMap: TEnterDrawWebContentTextMap = {
        optInCTAText: optinctatext
    };

    if (translationprefix) {
        textMap = {
            contactPopupKQCText: t('contact-popup.terms'),
            optedInCTAText: t('button.opted-in'),
            disabledCTAText: t('button.disabled'),
            optInCTAText: t('button.opt-in'),
            successPopupTitleText: t('success.title'),
            successPopupText: t('success.text'),
            successPopupCTAText: t('success.cta'),
            errorPopupTitleText: t('error.title'),
            errorPopupText: t('error.text'),
            errorPopupCTAText: t('error.cta')
        };
    }

    return (
        <EnterDrawBare
            mainTag={maintag}
            secondaryTags={secondaryTags}
            showLoginForGuest={showloginforguest}
            showContactPopup={showcontactpopup}
            ctaConfiguration={ctaConfiguration}
            navigateUrl={url}
            gameCode={gamecode}
            className={className}
            {...textMap}
        />
    );
};

export default EnterDraw;
